<template>
  <div class="i-career-requirements">
    <div class="wrap">
      <div class="caption">
        <iBaseHeader
          class="title"
          highlight-text=""
          text="Educational Degree"
          font-size-text="18px"
          align="start"
        />
        <div
          v-if="alternativeEducation?.description || nonAlternativeEducation.length"
          class="badge"
          :style="{ '--bg-color': educationIsOptional ? black400 : black950 }"
        >
          {{ educationIsOptional ? "Optional" : "Required" }}
        </div>
      </div>
      <div class="info">
        {{
          alternativeEducation?.description ||
          (nonAlternativeEducation.length ? educationDefaultText : "No education requirements found.")
        }}
      </div>
      <div
        v-if="nonAlternativeEducation.length"
        class="main"
      >
        <Carousel
          :autoplay="30000"
          :items-to-scroll="1"
          :items-to-show="isDesktop ? 2 : 1"
          class="reviews-carousel"
          wrap-around
          pause-autoplay-on-hover
          snap-align="start"
        >
          <Slide
            v-for="(entry, index) in nonAlternativeEducation"
            :key="index"
          >
            <div
              class="card with-link"
              @click="openUrl('https://degtrk.com/?a=3490&oc=5240&c=10607&s1=&c_id=640128e1d1a2d500139fd6a1')"
            >
              <div class="top">
                <div class="header">
                  {{ entry.schoolMajor }}
                  <div
                    v-if="!entry.isOptional"
                    class="badge"
                    :style="{ '--bg-color': sorbus400 }"
                  >
                    {{ entry.educationLevel }}
                  </div>
                </div>
                <div class="text">{{ entry.description }}</div>
              </div>
              <div class="link">Check your grant eligibility today. <img :src="iconArrowRightBlue" /></div>
            </div>
          </Slide>
          <template #addons>
            <navigation>
              <template #next>
                <span>
                  <img
                    :src="iconArrowRightGray"
                    alt="next-icon"
                  />
                </span>
              </template>
              <template #prev>
                <span>
                  <img
                    :src="iconArrowLeftGray"
                    alt="prev-icon"
                  />
                </span>
              </template>
            </navigation>
          </template>
        </Carousel>
      </div>
    </div>
    <div class="wrap">
      <div class="caption">
        <iBaseHeader
          class="title"
          highlight-text=""
          text="Certifications"
          font-size-text="18px"
          align="start"
        />
        <div
          v-if="alternativeCertification?.description || nonAlternativeCertification.length"
          class="badge"
          :style="{ '--bg-color': certificationIsOptional ? black400 : black950 }"
        >
          {{ certificationIsOptional ? "Optional" : "Required" }}
        </div>
      </div>
      <div class="info">
        {{
          alternativeCertification?.description ||
          (nonAlternativeCertification.length
            ? certificationDefaultText
            : "No certificate or license requirement found.")
        }}
      </div>
      <div
        v-if="nonAlternativeCertification.length"
        class="main"
      >
        <Carousel
          :autoplay="30000"
          :items-to-scroll="1"
          :items-to-show="isDesktop ? 2 : 1"
          class="reviews-carousel"
          wrap-around
          pause-autoplay-on-hover
          snap-align="start"
        >
          <Slide
            v-for="(entry, index) in nonAlternativeCertification"
            :key="index"
          >
            <div class="card">
              <div class="header">{{ entry.title }}</div>
              <div class="text">{{ entry.description }}</div>
            </div>
          </Slide>
          <template #addons>
            <navigation>
              <template #next>
                <span>
                  <img
                    :src="iconArrowRightGray"
                    alt="next-icon"
                  />
                </span>
              </template>
              <template #prev>
                <span>
                  <img
                    :src="iconArrowLeftGray"
                    alt="prev-icon"
                  />
                </span>
              </template>
            </navigation>
          </template>
        </Carousel>
      </div>
    </div>
    <div class="wrap">
      <div class="caption">
        <iBaseHeader
          highlight-text=""
          text="Experience"
          class="title"
          font-size-text="18px"
          align="start"
        />
        <div
          class="badge"
          :style="{ '--bg-color': azureRadiance600 }"
        >
          Recommended
        </div>
      </div>
      <div class="info">{{ activeJobExperience.experience }}</div>
      <div
        v-if="activeJobExperience.waysToGain.length"
        class="main"
      >
        <Carousel
          :autoplay="30000"
          :items-to-scroll="1"
          :items-to-show="isDesktop ? 2 : 1"
          class="reviews-carousel"
          wrap-around
          pause-autoplay-on-hover
          snap-align="start"
        >
          <Slide
            v-for="(entry, index) in activeJobExperience.waysToGain"
            :key="index"
          >
            <div class="card">
              <div class="header">Option {{ index + 1 }}</div>
              <div class="text">{{ entry }}</div>
            </div>
          </Slide>
          <template #addons>
            <navigation>
              <template #next>
                <span>
                  <img
                    :src="iconArrowRightGray"
                    alt="next-icon"
                  />
                </span>
              </template>
              <template #prev>
                <span>
                  <img
                    :src="iconArrowLeftGray"
                    alt="prev-icon"
                  />
                </span>
              </template>
            </navigation>
          </template>
        </Carousel>
        <iRequirementsBanner v-bind="BANNERS_DATA" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { Carousel, Navigation, Slide } from "vue3-carousel"
import { useReportStore } from "@/stores/report"
import { breakpointLg } from "@/styles/variables"
import { azureRadiance600, black400, black950, sorbus400 } from "@/styles/colors"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseHeader from "@/components/base/BaseHeader.vue"
import iRequirementsBanner from "@/components/report/careers/RequirementsBanner.vue"

// ASSETS
import ImageJobScan from "@/assets/careers/image-job-scan.png"
import iconArrowRightGray from "@/assets/icons/icon-arrow-right-gray.svg"
import iconArrowLeftGray from "@/assets/icons/icon-arrow-left-gray.svg"
import iconArrowRightBlue from "@/assets/icons/icon-blue-arrow-right.svg"

// TYPES
type BannerDataType = {
  buttonText: string
  description: string
  id: string
  image: string
  url: string
}

// STORES AND COMPOSABLES
const reportStore = useReportStore()
const isDesktop = useIsScreenSize(breakpointLg)

// CONSTANTS
const BANNERS_DATA: BannerDataType = {
  buttonText: "Go to JobScan",
  description: "Optimize your resume to transition to a",
  id: "go-to-jobscan",
  image: ImageJobScan,
  url: "https://jobscanco.pxf.io/jobtestorg"
}

// COMPUTED
const activeJob = computed(() => reportStore.getActiveJob)
const educationDefaultText = computed(
  () =>
    `To work as a ${activeJob.value.title}, you'll typically need one of following degrees to gain the necessary skills and knowledge for success.`
)
const certificationDefaultText = computed(
  () =>
    `To work as a ${activeJob.value.title}, you'll likely need one of the following certifications to meet industry standards and boost your skills.`
)

const activeJobQualifications = computed(() => {
  return reportStore.getActiveJobQualifications
})

const alternativeEducation = computed(() => {
  return activeJobQualifications.value?.education?.filter((entry) => entry.schoolMajor == "Alternative")?.[0]
})
const nonAlternativeEducation = computed(() => {
  return activeJobQualifications.value?.education?.filter((entry) => entry.schoolMajor != "Alternative")
})
const educationIsOptional = computed(() => {
  const jobEducation = activeJobQualifications.value?.education
  const nonAlternativeRequiredEducation = jobEducation?.filter(
    (entry) => !entry.isOptional && entry.schoolMajor !== "Alternative"
  )

  return !nonAlternativeRequiredEducation.length
})

const alternativeCertification = computed(() => {
  return activeJobQualifications.value?.certifications?.filter((entry) => entry.title == "Alternative")?.[0]
})
const nonAlternativeCertification = computed(() => {
  return activeJobQualifications.value?.certifications?.filter((entry) => entry.title != "Alternative")
})
const certificationIsOptional = computed(() => {
  const jobCertification = activeJobQualifications.value?.certifications
  const nonAlternativeRequiredCertification = jobCertification?.filter(
    (entry) => !entry.isOptional && entry.title !== "Alternative"
  )

  return !nonAlternativeRequiredCertification.length
})

const activeJobExperience = computed(() => {
  return {
    experience: activeJobQualifications.value?.relevantExperience?.experience,
    waysToGain: activeJobQualifications.value?.relevantExperience?.waysToGain
  }
})

// FUNCTIONS
const openUrl = (redirectUrl: string) => {
  window.open(redirectUrl, "_blank")
}
</script>

<style lang="scss" scoped>
.i-career-requirements {
  @include flex(column, $justify: center, $align: start);

  color: $pickle-bluewood900;
  margin-top: 20px;
  width: 100%;

  .badge {
    @include border-radius-lg;

    white-space: nowrap;
    padding: 2px 10px;
    color: $white;
    background-color: var(--bg-color);

    @include text-xs;

    font-weight: 500;
    width: fit-content;

    @media screen and (min-width: $breakpoint-sm) {
      @include text-sm;
    }
  }

  .wrap {
    width: 100%;

    &:not(:last-child) {
      margin-bottom: 30px;
    }

    .caption {
      @include flex(row, $justify: start, $align: center, $gap: 15px);

      margin-bottom: 10px;

      .title {
        width: fit-content;
      }
    }

    .info {
      @include text-md;

      font-weight: 500;
      color: $comet600;
    }

    .main {
      @include flex($direction: column, $justify: center, $align: center);

      padding: 30px 0 40px;
      border-bottom: 1px solid $zumthor100;

      .reviews-carousel {
        width: calc(100% - 25px);

        @media screen and (min-width: $breakpoint-sm) {
          width: 100%;
        }

        .card {
          @include flex(column, $justify: start, $align: start, $gap: 15px);

          width: 100%;
          height: 100%;
          padding: 24px;

          @include border-radius-lg;

          background-color: $white;
          border: 1px solid $zumthor100;
          text-align: start;

          @media screen and (min-width: $breakpoint-sm) {
            margin: 0 8px;
          }

          &.with-link {
            @include flex(column, $justify: space-between, $align: start, $gap: 15px);

            cursor: pointer;

            &:hover {
              background-color: $azure-radiance50;
            }
          }

          .top {
            @include flex(column, $justify: start, $align: start, $gap: 15px);
          }

          .header {
            @include text-lg;

            font-weight: 700;
            color: $pickle-bluewood900;

            @include flex(row, $justify: start, $align: center, $wrap: wrap, $gap: 6px 15px);

            @media screen and (min-width: $breakpoint-sm) {
              @include text-md;
            }
          }

          .text {
            @include text-sm;

            font-weight: 500;
            letter-spacing: 0;
            color: $comet600;
          }

          .link {
            @include flex(row, $justify: space-between, $align: center, $gap: 0);
            @include text-xs;

            font-weight: 500;
            letter-spacing: 0;
            color: $comet600;
            margin-top: 10px;
            width: 100%;

            &:last-child {
              padding-top: 10px;
            }

            &:not(:last-child) {
              border-bottom: 1px solid $zumthor100;
              padding-bottom: 10px;
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.i-career-requirements {
  /* stylelint-disable */
  .carousel__next,
  .carousel__prev {
    width: 15px;
    margin: 0 -18px;
  }
  /* stylelint-enable */
}

@media print {
  .i-career-requirements {
    .reviews-carousel {
      .card {
        margin: 0 8px;
      }
    }
  }
}
</style>
